import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Building2, Check, ArrowRight, ArrowLeft, Snowflake, Loader2, PartyPopper,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { value: "House", icon: Home },
  { value: "Flat / Apartment", icon: Building2 },
  { value: "Bungalow", icon: Home },
  { value: "Commercial", icon: Building2 },
];

const BEDROOMS = ["1", "2", "3", "4", "5+"];

const ROOMS = [
  "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4",
  "Living Room", "Kitchen / Diner", "Home Office", "Conservatory",
  "Loft / Attic", "Hallway / Landing", "Garage", "Whole Home",
];

const STEPS = ["Property", "Rooms", "Your details"];

const stepVariant = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

const OptionButton = ({ active, onClick, children, testId, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-3 rounded-xl border px-5 py-4 font-body text-left font-medium transition-[border-color,background-color,color] duration-200 active:scale-[0.98] ${
      active
        ? "border-brand-blue bg-brand-blue text-white"
        : "border-black/10 bg-white text-ink hover:border-brand-blue"
    }`}
  >
    {Icon && <Icon size={18} className={active ? "text-white" : "text-brand-blue"} />}
    <span className="flex-1">{children}</span>
    {active && <Check size={16} />}
  </button>
);

export const EnquiryBuilder = ({ imageUrl }) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    property_type: "",
    bedrooms: "",
    rooms: [],
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRoom = (r) =>
    setForm((f) => ({
      ...f,
      rooms: f.rooms.includes(r) ? f.rooms.filter((x) => x !== r) : [...f.rooms, r],
    }));

  const canNext =
    (step === 0 && form.property_type && form.bedrooms) ||
    (step === 1 && form.rooms.length > 0) ||
    step === 2;

  const validContact = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 5;

  const submit = async () => {
    if (!validContact) {
      toast.error("Please add your name, a valid email and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries/aircon`, form);
      setDone(true);
      toast.success("Enquiry sent — we'll be in touch shortly!");
    } catch (e) {
      toast.error("Something went wrong sending your enquiry. Please call us on 07881 746 762.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="aircon" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-stretch">
          {/* Left panel */}
          <div className="lg:col-span-4 rounded-2xl overflow-hidden relative border border-black/10 min-h-[320px]">
            <img src={imageUrl} alt="Air conditioning unit" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/10" />
            <div className="relative h-full flex flex-col justify-between p-8">
              <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 backdrop-blur px-4 py-1.5 font-body text-xs font-medium text-white">
                <Snowflake size={13} /> Air Conditioning
              </span>
              <div>
                <p className="font-display text-xs uppercase tracking-[0.25em] text-brand-yellow mb-3">
                  03 — Get a quote
                </p>
                <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl lg:text-5xl leading-[0.95]">
                  Cool every room, your way.
                </h2>
                <p className="font-body text-white/70 mt-4 leading-relaxed">
                  Tell us about your home and where you'd like units. We'll send a tailored quote — usually within 24 hours.
                </p>
              </div>
            </div>
          </div>

          {/* Right — form card */}
          <div className="lg:col-span-8 rounded-2xl border border-black/10 bg-white p-7 lg:p-10 flex flex-col">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center text-center py-16"
                data-testid="enquiry-success"
              >
                <div className="h-16 w-16 rounded-full bg-brand-yellow flex items-center justify-center mb-6">
                  <PartyPopper size={28} className="text-ink" />
                </div>
                <h3 className="font-display text-3xl font-extrabold tracking-tight text-ink">Enquiry sent!</h3>
                <p className="font-body text-ink/55 mt-3 max-w-md text-lg">
                  Thanks {form.name.split(" ")[0]} — your air con enquiry is with the JD Electrical team. We'll be in touch shortly.
                </p>
                <button
                  onClick={() => { setDone(false); setStep(0); setForm({ property_type: "", bedrooms: "", rooms: [], name: "", email: "", phone: "", message: "" }); }}
                  data-testid="enquiry-reset-btn"
                  className="mt-8 rounded-full border border-black/10 px-6 py-3 font-body text-sm font-semibold text-ink hover:border-brand-blue transition-colors duration-200"
                >
                  Start another enquiry
                </button>
              </motion.div>
            ) : (
              <>
                {/* Stepper */}
                <div className="flex items-center gap-2 mb-10">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-2 flex-1">
                      <div className={`flex items-center gap-2.5 ${i <= step ? "opacity-100" : "opacity-40"}`}>
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full font-display text-sm font-bold transition-colors duration-300 ${
                          i < step ? "bg-brand-blue text-white" : i === step ? "bg-ink text-white" : "bg-cloud text-ink border border-black/10"
                        }`}>
                          {i < step ? <Check size={14} /> : i + 1}
                        </span>
                        <span className="hidden sm:block font-body text-sm font-medium text-ink">{s}</span>
                      </div>
                      {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-brand-blue" : "bg-black/10"}`} />}
                    </div>
                  ))}
                </div>

                <div className="flex-1">
                  <AnimatePresence mode="wait">
                    {step === 0 && (
                      <motion.div key="s0" variants={stepVariant} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <label className="font-display text-lg font-bold text-ink block mb-4">What type of property is it?</label>
                        <div className="grid grid-cols-2 gap-3 mb-8">
                          {PROPERTY_TYPES.map((p) => (
                            <OptionButton key={p.value} icon={p.icon} active={form.property_type === p.value} onClick={() => set("property_type", p.value)} testId={`property-${p.value.split(" ")[0].toLowerCase()}`}>
                              {p.value}
                            </OptionButton>
                          ))}
                        </div>
                        <label className="font-display text-lg font-bold text-ink block mb-4">How many bedrooms?</label>
                        <div className="grid grid-cols-5 gap-3">
                          {BEDROOMS.map((b) => (
                            <button key={b} type="button" onClick={() => set("bedrooms", b)} data-testid={`bedrooms-${b.replace("+", "plus")}`}
                              className={`rounded-xl border py-4 font-display text-xl font-bold transition-[border-color,background-color,color] duration-200 active:scale-95 ${
                                form.bedrooms === b ? "border-brand-blue bg-brand-blue text-white" : "border-black/10 bg-white text-ink hover:border-brand-blue"
                              }`}>
                              {b}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 1 && (
                      <motion.div key="s1" variants={stepVariant} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <div className="flex items-baseline justify-between mb-4">
                          <label className="font-display text-lg font-bold text-ink">Which rooms need units?</label>
                          <span className="font-body text-sm text-ink/50" data-testid="rooms-count">{form.rooms.length} selected</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {ROOMS.map((r) => {
                            const active = form.rooms.includes(r);
                            return (
                              <button key={r} type="button" onClick={() => toggleRoom(r)} data-testid={`room-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                className={`flex items-center justify-between gap-2 rounded-xl border px-4 py-3.5 font-body text-sm font-medium text-left transition-[border-color,background-color,color] duration-200 active:scale-[0.98] ${
                                  active ? "border-brand-blue bg-brand-blue text-white" : "border-black/10 bg-white text-ink hover:border-brand-blue"
                                }`}>
                                <span>{r}</span>
                                <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${active ? "bg-white border-white" : "border-black/20"}`}>
                                  {active && <Check size={13} className="text-brand-blue" />}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div key="s2" variants={stepVariant} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="font-body text-sm font-medium text-ink/70 block mb-2">Full name</label>
                            <input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith"
                              className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors duration-200" />
                          </div>
                          <div>
                            <label className="font-body text-sm font-medium text-ink/70 block mb-2">Phone number</label>
                            <input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07123 456789"
                              className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors duration-200" />
                          </div>
                        </div>
                        <div>
                          <label className="font-body text-sm font-medium text-ink/70 block mb-2">Email address</label>
                          <input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com"
                            className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors duration-200" />
                        </div>
                        <div>
                          <label className="font-body text-sm font-medium text-ink/70 block mb-2">Anything else? (optional)</label>
                          <textarea data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Preferred timescales, questions, access notes…"
                            className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors duration-200 resize-none" />
                        </div>
                        <div className="rounded-xl bg-cloud border border-black/10 p-4 font-body text-sm text-ink/60">
                          <span className="font-semibold text-ink">Summary:</span> {form.property_type || "—"}, {form.bedrooms || "—"} bed · Units in {form.rooms.length} room{form.rooms.length === 1 ? "" : "s"}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Nav */}
                <div className="flex items-center justify-between mt-10 pt-6 border-t border-black/10">
                  <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="enquiry-back-btn"
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold text-ink/60 hover:text-ink disabled:opacity-0 transition-colors duration-200">
                    <ArrowLeft size={16} /> Back
                  </button>

                  {step < 2 ? (
                    <button type="button" onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="enquiry-next-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 font-body text-sm font-semibold text-white hover:bg-brand-blue active:scale-95 disabled:opacity-30 disabled:hover:bg-ink transition-[background-color,transform,opacity] duration-200">
                      Continue <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button type="button" onClick={submit} disabled={submitting} data-testid="enquiry-submit-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 font-body text-sm font-semibold text-white hover:bg-ink active:scale-95 disabled:opacity-60 transition-[background-color,transform] duration-200">
                      {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send enquiry <ArrowRight size={16} /></>}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
